import {useMemo, useRef, useState} from "react";
import {Alert} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {bffApi} from "../../chat/bffApi";
import {
    buildDocumentsExportUrl,
    downloadExport,
    sendExportMail,
} from "../checklist/exportHelpers";
import {ResourceType} from "../types";
import {ChecklistModalActions} from "../checklist/ChecklistModalActions";
import {
    CommentsToggle,
    DeliveryControls,
    DocumentsList,
    TemplateSelect,
} from "./parts";
import {setStoredTemplate, setStoredTopicTemplate} from "./templateStorage";
import type {
    CollectionMeta,
    DeliveryFormat,
    ExportDocument,
    TopicMeta,
} from "./types";
import classes from "../checklist/modalStyles.module.css";

type Props = {
    onClose: () => void;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    documents: ExportDocument[];
};

export const GenerateTemplatesTab = ({
    onClose,
    topicMeta,
    collectionMeta,
    documents,
}: Props) => {
    const {t} = useTranslation();
    const {data: home} = dibAppApi.endpoints.getHome.useQuery();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const isdemo = !!user?.demokonto;
    const defaultEmail = user?.epost ?? "";
    const templates = home?.templates ?? [];

    const [finalDocuments, setFinalDocuments] = useState(documents);
    const count = useMemo(
        () => finalDocuments.filter((d) => d.selected).length,
        [finalDocuments]
    );

    const [selectedTemplate, setSelectedTemplate] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [validTemplate, setValidTemplate] = useState(true);
    const [format, setFormat] = useState<DeliveryFormat>("doc");
    const [recipients, setRecipients] = useState<string[]>([defaultEmail]);
    const [withComments, setWithComments] = useState(false);
    const templateRef = useRef<HTMLDivElement>(null);

    const version = new URLSearchParams(window.location.search).get("version");

    const hasSelectableNonPdf =
        finalDocuments.filter(
            (d) => d.selected && d.resourceTypeId !== ResourceType.pdf
        ).length > 0;
    const enabled = isValid && hasSelectableNonPdf;

    const handleTemplateSelect = (id: string) => {
        setSelectedTemplate(id);
        setValidTemplate(true);
    };

    const runGenerate = async () => {
        if (isdemo || !isValid) return;
        const selectedDocs = finalDocuments.filter((d) => d.selected);
        const formatParam = format === "doc" || format === "email" ? "docx" : "zip";
        const url = buildDocumentsExportUrl({
            format: formatParam,
            topicId: topicMeta.resourceId,
            collectionId: collectionMeta?.id,
            topics: selectedDocs.map((d) => d.id),
            withComments,
            version: version ?? undefined,
            template: selectedTemplate ?? "0",
            email: format === "email" ? recipients[0] : undefined,
        });

        setStoredTemplate(selectedTemplate);
        setStoredTopicTemplate(topicMeta.topicId, selectedTemplate);

        setIsLoading(true);
        try {
            if (format === "email") {
                await sendExportMail(url);
            } else {
                await downloadExport(url);
            }
            onClose();
        } catch {
            setIsLoading(false);
        }
    };

    const handlePrimaryClick = () => {
        if (selectedTemplate === undefined && templates.length !== 0) {
            setValidTemplate(false);
            templateRef.current?.scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: "nearest",
            });
            return;
        }
        void runGenerate();
    };

    return (
        <>
            <div className={classes.modalBody}>
                {isdemo && (
                    <Alert variant="light" color="gray">
                        {t("Info.GenerationNotAvailableForDemoUsers")}
                    </Alert>
                )}
                <DocumentsList
                    areFilesAllowed={false}
                    count={count}
                    documents={finalDocuments}
                    setDocuments={setFinalDocuments}
                    setIsValid={setIsValid}
                />
                <DeliveryControls
                    format={format}
                    onSetFormat={setFormat}
                    recipients={recipients}
                    onSetRecipients={setRecipients}
                />
                <div ref={templateRef}>
                    <TemplateSelect
                        selectedTemplate={selectedTemplate}
                        onSelect={handleTemplateSelect}
                        error={!validTemplate}
                    />
                </div>
                <CommentsToggle
                    withComments={withComments}
                    setWithComments={setWithComments}
                />
            </div>
            <ChecklistModalActions
                primary={{
                    label: t("Action.GenerateTemplates"),
                    onClick: handlePrimaryClick,
                    loading: isLoading,
                    disabled: isdemo || !enabled,
                }}
                secondary={{label: t("Word.Abort"), onClick: onClose}}
            />
        </>
    );
};
